import { defineStore } from 'pinia'
import playerLogsApi from '../api/playerLogsApi'
import { PlayerLogDescription } from '../enums/playerLogDescription'
import {
    mEvent,
    VD_AGENT_CONNECTED,
    VD_AGENT_DISCONNECTED,
} from '../utils/events'
import { i18n } from '../utils/i18n'
import { useCursorStore } from './cursor'
import { useStore } from './main'
import { useStreamStore } from './stream'
import { useToastStore } from './toast'

const INITIAL_GRACE_PERIOD_MS = 15_000
const RECONNECT_GRACE_PERIOD_MS = 60_000

interface State {
    warningToastId: number | null
    warningTimeout: number | null
    isEnabled: boolean
}

export const useVdAgentStore = () => {
    const toastStore = useToastStore()
    const cursorStore = useCursorStore()
    const mainStore = useStore()
    const streamStore = useStreamStore()

    function _scheduleWarning(delayMs: number): void {
        if (instance.warningTimeout) {
            clearTimeout(instance.warningTimeout)
        }

        instance.warningTimeout = window.setTimeout(() => {
            instance.warningTimeout = null

            if (instance.isEnabled) {
                return
            }

            _showWarning()
        }, delayMs)
    }

    function _cancelScheduledWarning(): void {
        if (!instance.warningTimeout) {
            return
        }

        clearTimeout(instance.warningTimeout)
        instance.warningTimeout = null
    }

    function _showWarning(): void {
        if (instance.isEnabled || instance.warningToastId !== null) {
            return
        }

        const degradedFeatures = [
            i18n.t('common.label.mouseFunctionality'),
            i18n.t('common.label.fileTransfer'),
        ]
            .map((feature) => `• ${feature}`)
            .join('<br>')

        instance.warningToastId = toastStore.toastWarning(
            i18n.t('common.label.spiceDriver'),
            i18n.t('common.sentence.spiceDriverDegradedPerformance', [
                degradedFeatures,
            ])
        )
    }

    function _hideWarning(): void {
        if (instance.warningToastId === null) {
            return
        }

        toastStore.remove(instance.warningToastId)
        instance.warningToastId = null
    }

    function _sendVdAgentLog(description: PlayerLogDescription): void {
        playerLogsApi.sendVdAgentLog({
            proxmox_streaming_token: streamStore.streamingToken.token,
            description,
        })
    }

    const store = defineStore('vdAgent', {
        state: (): State => ({
            warningToastId: null,
            warningTimeout: null,
            isEnabled: false,
        }),

        actions: {
            init(): void {
                this.isEnabled = false
                this.warningToastId = null

                mEvent.subscribe(VD_AGENT_CONNECTED, async () => {
                    this.isEnabled = true
                    _cancelScheduledWarning()
                    _hideWarning()
                    _sendVdAgentLog(PlayerLogDescription.CONNECT_VD_AGENT)

                    // The agent is now available: re-establish the cursor
                    // captured mode from the user preference, which may have
                    // been force-enabled while the agent was still connecting.
                    await cursorStore.initRemoteCursorCaptureMode()
                })

                mEvent.subscribe(VD_AGENT_DISCONNECTED, async () => {
                    this.isEnabled = false

                    await cursorStore.enableRemoteCursorCapturedMode()

                    mainStore.updateLockState()

                    _scheduleWarning(RECONNECT_GRACE_PERIOD_MS)
                    _sendVdAgentLog(PlayerLogDescription.DISCONNECT_VD_AGENT)
                })

                _scheduleWarning(INITIAL_GRACE_PERIOD_MS)
            },
        },
    })

    const instance = store()

    return instance
}
